import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  listEventMessages, getEventMessagesConfig, createEventMessage, patchEventMessage,
  deleteEventMessage, publishEventMessage, skipEventMessage,
  uploadEventImage, fetchEventImageObjectUrl,
} from '../lib/api.js'
import ErrorAlert from '../components/ErrorAlert.jsx'
import { PAGE_STANDARD } from '../lib/layout.js'
import { formatLocalDateTime, venueLocalFromInstant, instantFromVenueLocal } from '../lib/datetime.js'
import {
  BTN_PRIMARY, BTN_COMPACT_PRIMARY, BTN_COMPACT_GHOST, BTN_COMPACT_BRAND, INPUT,
} from '../lib/ui.js'

// The AMA announcement back office (#377), over the svc surface added in #376.
//
// Replaces editing a Python dict on the api VM and redeploying a script. Every
// message here is a real post to JGP's personal LinkedIn feed, so the page is
// built to make the irreversible things hard to do by accident and the
// dangerous state impossible to miss.

// The schedule is agreed in ET — "Monday 09:00 ET", "30 minutes before" — so
// that is what the editor shows, whatever zone the browser is in.
const SCHEDULE_TZ = 'America/New_York'

// LinkedIn truncates the feed preview around here. Not a hard limit; a fold.
const FOLD = 210
const MAX_BODY = 3000

const STATUS_CHIP = {
  DRAFT: 'bg-slate-100 text-slate-700',
  SCHEDULED: 'bg-sky-100 text-sky-800',
  PUBLISHED: 'bg-emerald-100 text-emerald-800',
  SKIPPED: 'bg-amber-100 text-amber-900',
  FAILED: 'bg-rose-100 text-rose-800',
}

function firstLine(body) {
  const line = String(body || '').split('\n').find((l) => l.trim()) || ''
  return line.length > 120 ? `${line.slice(0, 120)}…` : line
}

// The image bytes are behind an admin-only endpoint, so <img src> cannot fetch
// them — a bare request carries no bearer token. Pull them through the
// authenticated client into an object URL, and hand it back on unmount.
function Thumbnail({ image }) {
  const [url, setUrl] = useState(null)
  useEffect(() => {
    if (!image?.id) { setUrl(null); return undefined }
    let revoked = false
    let objectUrl = null
    fetchEventImageObjectUrl(image.id)
      .then((u) => {
        if (revoked) { URL.revokeObjectURL(u); return }
        objectUrl = u
        setUrl(u)
      })
      .catch(() => setUrl(null))
    return () => {
      revoked = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [image?.id])

  if (!image) return null
  return url
    ? <img src={url} alt={image.altText || image.filename}
           className="h-16 w-16 rounded-lg object-cover ring-1 ring-slate-200" />
    : <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-slate-100 text-[10px] text-slate-400 ring-1 ring-slate-200">
        {image.filename}
      </div>
}

function MessageRow({ message, onChanged, onError, publishEnabled }) {
  const published = message.status === 'PUBLISHED'
  const [body, setBody] = useState(message.body || '')
  const [when, setWhen] = useState(venueLocalFromInstant(message.scheduledAtUtc, SCHEDULE_TZ))
  const [busy, setBusy] = useState(false)

  // The server is the only source of truth for status. Never render an
  // optimistic "published" — a message that failed on the wire must not look
  // like it went out.
  useEffect(() => {
    setBody(message.body || '')
    setWhen(venueLocalFromInstant(message.scheduledAtUtc, SCHEDULE_TZ))
  }, [message.id, message.body, message.scheduledAtUtc])

  const scheduledIso = instantFromVenueLocal(when, SCHEDULE_TZ)
  const isPast = Boolean(scheduledIso) && new Date(scheduledIso).getTime() < Date.now()
  const bodyDirty = body !== (message.body || '')
  // Compared as instants, not strings: a round trip through the ET input
  // yields "…:00.000Z" where the server sent "…:00Z", so a string compare
  // reports every untouched message as edited and PATCHes a redundant time.
  const whenDirty = Boolean(scheduledIso)
    && new Date(scheduledIso).getTime() !== new Date(message.scheduledAtUtc).getTime()
  const dirty = bodyDirty || whenDirty
  const tooLong = body.length > MAX_BODY

  const run = async (fn) => {
    setBusy(true)
    onError(null)
    try {
      const updated = await fn()
      onChanged(updated)
    } catch (err) {
      onError(err.message || String(err))
    } finally {
      setBusy(false)
    }
  }

  const save = () => run(() => patchEventMessage(message.id, {
    ...(bodyDirty ? { body } : {}),
    ...(whenDirty ? { scheduledAtUtc: scheduledIso } : {}),
  }))

  const publish = () => {
    // This posts to a real public feed. A misclick is public and permanent.
    const ok = window.confirm(
      `Publish to LinkedIn now?\n\n${message.eventTitle}\n"${firstLine(message.body)}"\n\n`
      + 'This posts to the live personal feed immediately and cannot be undone.')
    if (!ok) return
    run(() => publishEventMessage(message.id))
  }

  const attach = async (file) => {
    if (!file) return
    await run(async () => {
      const image = await uploadEventImage(file, {
        eventId: message.eventId,
        altText: message.eventTitle,
      })
      return patchEventMessage(message.id, { imageId: image.id })
    })
  }

  return (
    <li className="px-4 py-4">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_CHIP[message.status] || STATUS_CHIP.DRAFT}`}>
          {message.status}
        </span>
        {message.label ? <span className="text-xs font-medium text-slate-500">{message.label}</span> : null}
        <span className="text-xs text-slate-400">
          {published
            ? `Published ${formatLocalDateTime(message.publishedAtUtc)}`
            : `Scheduled ${formatLocalDateTime(message.scheduledAtUtc)}`}
        </span>
        {message.postUrl ? (
          <a href={message.postUrl} target="_blank" rel="noreferrer"
             className="text-xs font-medium text-brand-700 hover:underline">
            View on LinkedIn
          </a>
        ) : null}
      </div>

      {message.status === 'FAILED' && message.lastError ? (
        // In full, deliberately: a truncated LinkedIn error is useless.
        <pre className="mb-2 whitespace-pre-wrap rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-900 ring-1 ring-rose-200">
          {message.lastError}
        </pre>
      ) : null}

      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="flex-1">
          {published ? (
            <p className="whitespace-pre-wrap rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
              {message.body}
            </p>
          ) : (
            <>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={6}
                aria-label="Message body"
                className={`w-full ${INPUT}`}
              />
              <div className="mt-1 flex flex-wrap gap-3 text-xs">
                <span className={tooLong ? 'font-medium text-rose-700' : 'text-slate-500'}>
                  {body.length} / {MAX_BODY} characters
                </span>
                <span className={body.length > FOLD ? 'text-amber-700' : 'text-slate-400'}>
                  {body.length > FOLD
                    ? `${body.length - FOLD} characters past the ~${FOLD}-character feed fold`
                    : `${FOLD - body.length} characters before the feed fold`}
                </span>
              </div>
            </>
          )}
        </div>

        <div className="w-full sm:w-56">
          {published ? null : (
            <label className="block text-xs">
              <span className="font-medium text-slate-600">Goes out (ET)</span>
              <input
                type="datetime-local"
                value={when}
                onChange={(e) => setWhen(e.target.value)}
                aria-label="Scheduled time (ET)"
                className={`mt-1 w-full ${INPUT}`}
              />
              {isPast ? (
                <span className="mt-1 block font-medium text-amber-700">
                  That time is in the past — it will go out on the next pass, or be
                  skipped if it is past its grace window.
                </span>
              ) : null}
            </label>
          )}
          <div className="mt-3 flex items-center gap-2">
            <Thumbnail image={message.image} />
            {published ? null : (
              <div className="flex flex-col gap-1">
                <label className={`${BTN_COMPACT_GHOST} cursor-pointer`}>
                  {message.image ? 'Replace' : 'Add image'}
                  <input type="file" accept="image/*" className="hidden"
                         aria-label="Attach an image"
                         onChange={(e) => attach(e.target.files?.[0])} />
                </label>
                {message.image ? (
                  <button type="button" className={BTN_COMPACT_GHOST} disabled={busy}
                          onClick={() => run(() => patchEventMessage(message.id, { clearImage: true }))}>
                    Remove
                  </button>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>

      {published ? null : (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button type="button" className={BTN_COMPACT_PRIMARY}
                  disabled={busy || !dirty || tooLong} onClick={save}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          {message.status === 'DRAFT' ? (
            <button type="button" className={BTN_COMPACT_BRAND} disabled={busy}
                    onClick={() => run(() => patchEventMessage(message.id, { status: 'SCHEDULED' }))}>
              Mark ready
            </button>
          ) : null}
          <button type="button" className={BTN_COMPACT_BRAND} disabled={busy || !publishEnabled}
                  title={publishEnabled ? undefined : 'Publishing is switched off on the server'}
                  onClick={publish}>
            Publish now
          </button>
          {message.status === 'SKIPPED' ? (
            <button type="button" className={BTN_COMPACT_GHOST} disabled={busy}
                    onClick={() => run(() => patchEventMessage(message.id, { status: 'DRAFT' }))}>
              Un-skip
            </button>
          ) : (
            <button type="button" className={BTN_COMPACT_GHOST} disabled={busy}
                    onClick={() => run(() => skipEventMessage(message.id))}>
              Skip
            </button>
          )}
          <button type="button" className={`${BTN_COMPACT_GHOST} text-rose-700 hover:bg-rose-50`}
                  disabled={busy}
                  onClick={() => {
                    if (!window.confirm(`Delete this ${message.label || 'message'} for ${message.eventTitle}?`)) return
                    run(async () => { await deleteEventMessage(message.id); return null })
                  }}>
            Delete
          </button>
        </div>
      )}
    </li>
  )
}

export default function AdminMessagesPage() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [publishEnabled, setPublishEnabled] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    Promise.all([
      listEventMessages(),
      getEventMessagesConfig().catch(() => ({ publishEnabled: false })),
    ])
      .then(([messages, config]) => {
        setRows(Array.isArray(messages) ? messages : [])
        setPublishEnabled(Boolean(config?.publishEnabled))
      })
      .catch((err) => setError(err.message || String(err)))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  // Grouped by episode, episodes by start date, messages by scheduled time.
  // An event may carry one message or ten — nothing here assumes three.
  const episodes = useMemo(() => {
    const byEvent = new Map()
    for (const m of rows) {
      if (!byEvent.has(m.eventId)) {
        byEvent.set(m.eventId, { id: m.eventId, title: m.eventTitle, startsAtUtc: m.eventStartsAtUtc, messages: [] })
      }
      byEvent.get(m.eventId).messages.push(m)
    }
    const list = [...byEvent.values()]
    for (const e of list) {
      e.messages.sort((a, b) => String(a.scheduledAtUtc).localeCompare(String(b.scheduledAtUtc)))
    }
    list.sort((a, b) => String(a.startsAtUtc).localeCompare(String(b.startsAtUtc)))
    return list
  }, [rows])

  // Always re-read from the server rather than patching state optimistically:
  // the status is the one thing on this page that must never be a guess.
  const onChanged = () => load()

  const addMessage = async (episode) => {
    setError(null)
    try {
      await createEventMessage({
        eventId: episode.id,
        label: 'New',
        body: 'Write the announcement here.',
        // An hour before the episode is a sane default that is never
        // accidentally already due.
        scheduledAtUtc: new Date(new Date(episode.startsAtUtc).getTime() - 3600_000).toISOString(),
      })
      load()
    } catch (err) {
      setError(err.message || String(err))
    }
  }

  return (
    <main className={`${PAGE_STANDARD} pb-12 pt-8 sm:pt-10`}>
      <header className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          <span className="brand-gradient-text">Messages</span>
        </h1>
        <p className="mt-2 max-w-3xl text-base text-slate-600">
          The LinkedIn announcements for each episode. Edit the text, move when it goes out,
          attach an image, or send one now. An episode can carry as many messages as it needs.
        </p>
      </header>

      {/* The single most important thing on this page: what will happen on its
          own, without anyone clicking anything. */}
      <div className={`mb-6 rounded-lg border px-4 py-3 text-sm ${publishEnabled
        ? 'border-amber-200 bg-amber-50 text-amber-900'
        : 'border-slate-200 bg-slate-50 text-slate-700'}`}>
        {publishEnabled ? (
          <>
            <strong>Publishing is live.</strong> Every message below goes out automatically at its
            scheduled time — <strong>including drafts</strong>. Skip is the only thing that holds one back.
          </>
        ) : (
          <>
            <strong>Publishing is switched off on the server.</strong> Nothing here will reach LinkedIn,
            scheduled or otherwise, until it is switched on. Edit freely.
          </>
        )}
      </div>

      {error ? <ErrorAlert title="Message management error" message={error} source="AdminMessagesPage" /> : null}

      {loading ? (
        <div className="rounded-2xl bg-white p-8 text-center text-slate-500 shadow-sm ring-1 ring-slate-200/60">
          Loading…
        </div>
      ) : episodes.length === 0 ? (
        <div className="rounded-2xl bg-white p-10 text-center shadow-sm ring-1 ring-slate-200/60">
          <h3 className="text-base font-semibold text-slate-900">No messages yet</h3>
          <p className="mt-1 text-sm text-slate-500">
            Announcements are seeded for each AMA episode on the calendar.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {episodes.map((episode) => (
            <section key={episode.id} className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200/60">
              <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 bg-slate-50 px-4 py-3">
                <div>
                  <h2 className="text-sm font-semibold text-slate-900">{episode.title}</h2>
                  <p className="text-xs text-slate-500">
                    {formatLocalDateTime(episode.startsAtUtc)} · {episode.messages.length} message
                    {episode.messages.length === 1 ? '' : 's'}
                  </p>
                </div>
                <button type="button" className={BTN_PRIMARY} onClick={() => addMessage(episode)}>
                  Add message
                </button>
              </header>
              <ul className="divide-y divide-slate-100">
                {episode.messages.map((m) => (
                  <MessageRow key={m.id} message={m} onChanged={onChanged} onError={setError}
                              publishEnabled={publishEnabled} />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </main>
  )
}
